'use client';

import { c as _c } from "react/compiler-runtime";
import type { SidebarChatMessage } from '@/components/chat/types/sidebar-chat-message';
import type { Message as DbMessage } from '@/generated/prisma/browser';
import { useQuery } from '@tanstack/react-query';
import { messagesQueryKey } from '@/data/messages/query-keys';
import { convertToUIMessages } from '@/lib/utils';

/**
 * Fetches persisted messages for a chat and converts them to the UI message
 * shape consumed by the AI SDK. Centralizing the conversion here lets callers
 * share a single cache entry with the Chat component — including cache writes
 * from Chat's `onFinish` handler via `queryClient.setQueryData`.
 */
export const fetchMessages = async (chatId: string): Promise<Array<SidebarChatMessage>> => {
  const response = await fetch(`/api/messages?chatId=${chatId}`, {
    method: 'POST'
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch messages: ${response.status} ${response.statusText}`);
  }
  const data = (await response.json()) as Array<DbMessage>;
  return convertToUIMessages(data) as Array<SidebarChatMessage>;
};
export const useMessages = (chatId, t0) => {
  const $ = _c(8);
  const isChatPersisted = t0 === undefined ? true : t0;
  const isEnabled = Boolean(chatId) && isChatPersisted;
  let t1;
  if ($[0] !== chatId) {
    t1 = chatId ? messagesQueryKey(chatId) : ["messages"];
    $[0] = chatId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== chatId) {
    t2 = () => fetchMessages(chatId as string);
    $[2] = chatId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== isEnabled || $[5] !== t1 || $[6] !== t2) {
    t3 = {
      queryKey: t1,
      queryFn: t2,
      enabled: isEnabled,
      retry: _temp,
      staleTime: 300000
    };
    $[4] = isEnabled;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useQuery(t3);
};
function _temp(failureCount) {
  return failureCount < 2;
}